<script setup>
  import { computed } from 'vue';
  import Rating from 'common/components/Rating';

  const props = defineProps({
    review: {
      type: Object,
      required: true,
    },
    showUserInfo: {
      type: Boolean,
      default: true,
    },
    maxCommentLength: {
      type: Number,
      default: 200,
    },
  });

  const reviewData = computed(() => ({
    comment: props.review.review || '',
    userName: props.review.username || 'Usuario Anónimo',
    userAvatar: props.review.profile_image || null,
    rating: props.review.satisfaction || 0,
    date:
      props.review.date_completed ||
      props.review.reservation_date ||
      new Date().toISOString(),
    activity: props.review.activity?.name || '',
  }));

  const activityName = computed(() => {
    return props.review.activity?.name || 'Reseñas destacadas';
  });

  const truncatedComment = computed(() => {
    const comment = reviewData.value.comment;
    if (!comment) return '';
    if (comment.length <= props.maxCommentLength) {
      return comment;
    }
    return comment.substring(0, props.maxCommentLength) + '...';
  });

  const userInitials = computed(() => {
    if (reviewData.value.userAvatar) return '';

    const userName = reviewData.value.userName;
    const names = userName.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return userName[0].toUpperCase();
  });

  const formatDate = computed(() => {
    const date = new Date(reviewData.value.date);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  });
</script>

<template>
  <div class="review-card-wrapper">
    <div class="full-width">
      <div class="card-header">
        <div class="activity-info">
          <span class="activity-name">{{ activityName }}</span>
          <span class="activity-date" v-if="reviewData.date">
            {{ formatDate }}
          </span>
        </div>
      </div>

      <div class="review-card">
        <div class="rating-container">
          <Rating
            :value="reviewData.rating"
            :maxValue="5"
            :allStars="true"
            starSize="--medium"
            :showStart="true"
            :showRatingNumber="false"
            class="rating-stars"
          />
        </div>

        <div class="comment-container">
          <p class="comment-text">"{{ truncatedComment }}"</p>
        </div>
      </div>
    </div>

    <div class="user-info" v-if="showUserInfo">
      <div class="user-avatar">
        <img
          v-if="reviewData.userAvatar"
          :src="reviewData.userAvatar"
          :alt="`Avatar de ${reviewData.userName}`"
          class="avatar-image"
        />
        <div v-else class="avatar-placeholder">
          {{ userInitials }}
        </div>
      </div>

      <div class="user-details">
        <span class="user-name">{{ reviewData.userName }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '~assets/styles/index.scss';

  .review-card-wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 16px;
    box-sizing: border-box;
    @media (max-width: $screen-sm-max) {
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      justify-content: space-between;
    }
  }

  .card-header {
    margin-bottom: 16px;
    text-align: left;

    @media (max-width: $screen-xs-max) {
      margin-bottom: 12px;
    }
  }

  .activity-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 16px;
    flex-wrap: wrap;
  }

  .activity-name {
    font-weight: 400;
    color: #666;
  }

  .activity-date {
    color: #666;

    &::before {
      content: '|';
      margin-right: 8px;
    }
  }

  .review-card {
    background: white;
    border-radius: 20px 20px 20px 0;
    padding: 24px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border: 1px solid #e6e6e6;
    overflow: hidden;
    box-sizing: border-box;
  }

  .rating-container {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
  }

  .rating-stars {
    ::v-deep .star {
      color: #ffc107;
      font-size: 20px;
    }
  }

  .comment-container {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .comment-text {
    color: #222;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Figtree;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    margin: 0;
    quotes: none;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;

    &::before,
    &::after {
      content: '';
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-direction: row;
    @media (max-width: $screen-sm-max) {
      flex-direction: row;
      min-height: 40px;
      margin-top: auto;
      padding-top: 16px;
    }
  }

  .user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 18px;
  }

  .user-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .user-name {
    font-weight: 600;
    color: #333;
  }

  .review-date {
    font-size: 16px;
    color: #666;
  }

  // Responsive design
  @media (max-width: $screen-sm-max) {
    .review-card {
      padding: 16px;
      height: 180px;
      box-sizing: border-box;
    }

    .comment-text {
      font-size: 16px;
      -webkit-line-clamp: 4;
      line-clamp: 4;
    }

    .user-avatar {
      width: 40px;
      height: 40px;
    }

    .avatar-placeholder {
      font-size: 16px;
    }
    .user-info {
      width: fit-content;
    }
  }

  @media (max-width: $screen-xs-max) {
    .review-card {
      padding: 20px;
      width: 100%;
      height: 180px;
      box-sizing: border-box;
    }

    .comment-text {
      font-size: 16px;
      -webkit-line-clamp: 3;
      line-clamp: 3;
    }

    .user-avatar {
      width: 40px;
      height: 40px;
    }

    .avatar-placeholder {
      font-size: 16px;
    }

    .review-date {
      font-size: 16px;
    }
  }
</style>
